/* eslint-disable react/jsx-one-expression-per-line */
import {
  Card,
  CardHeader,
  Collapse,
  IconButton,
  Typography,
  Grid,
} from '@mui/material'
import React, { useState, useEffect, useCallback } from 'react'
import InternalContent from '~/components/Layout/components/InternalContent'
import { useLang } from '~/hooks/useLang'
import { useHookstate } from '@hookstate/core'
import {
  DataGrid,
  type GridColDef,
  type GridRowsProp,
  gridClasses,
  type GridSortModel,
} from '@mui/x-data-grid'
import { ptBR } from '@mui/x-data-grid/locales'
import dayjs from 'dayjs'
import notify from '~/utils/notify'
import { getInterestPointSearchLogs } from '../services'
import AuditInterestPointFilters from './AuditInterestPointFilters'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import Tooltip from '@mui/material/Tooltip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'

interface InterestPointLog {
  readonly id: number
  readonly action: string
  readonly created_at: string
  readonly 'interest_point:name': string
  readonly 'user:representation_name': string
  readonly origin: string
}

// eslint-disable-next-line max-lines-per-function
export default function ListAllAuditInterestPoint() {
  const { translate } = useLang()
  const labelButton = useHookstate(translate('Search'))
  const selectAction = useHookstate<string | null>(null)
  const loadingButton = useHookstate<boolean>(false)
  const expandFilterSearch = useHookstate<boolean>(true)
  const userId = useHookstate<number | null>(null)
  const resetValeus = useHookstate(false)
  const focusChange = useHookstate<boolean>(false)
  const initialPeriod = useHookstate<string>('')
  const originType = useHookstate<string | null>(null)
  const selectedDevice = useHookstate<number | null>(null)
  const selectedStations = useHookstate<number | null>(null)

  const [startDate, setStartDate] = useState<string | null>(null)
  const [finishDate, setFinishDate] = useState<string | null>(null)
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [rowCount, setRowCount] = React.useState(0)
  const [sortModel, setSortModel] = React.useState<GridSortModel>([])
  const tableMode = 'server'
  const limit = 15
  const expandInterestPoints = useHookstate<boolean>(false)
  const [paginationModel, setPaginationModel] = React.useState<{
    readonly page: number
    readonly pageSize: number
  }>({ page: 0, pageSize: limit })
  const [openModal, setOpenModal] = useState(false)
  const [selectedRow, setSelectedRow] = useState<InterestPointLog | null>(null)

  const handleView = (row: InterestPointLog) => {
    setSelectedRow(row)
    setOpenModal(true)
  }

  const clickOutModalClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      setOpenModal(false)
    }
  }

  const columns: readonly GridColDef[] = [
    { field: 'id' },
    {
      field: 'action',
      headerName: translate('action'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value) => {
        return translate(value)
      },
    },
    {
      field: 'interest_point:name',
      headerName: translate('Interest point'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'user:representation_name',
      headerName: translate('User'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'origin',
      headerName: translate('Origin'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'created_at',
      headerName: translate('Performed at'),
      type: 'date',
      flex: 1,
      filterable: false,
      sortable: true,
      disableColumnMenu: false,
      valueFormatter: (value) => {
        return dayjs(value as string).format('DD/MM/YYYY HH:mm')
      },
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      align: 'center',
      width: 80,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Tooltip title={translate('Visualizar')}>
            <IconButton
              onClick={() => handleView(params.row)}
              size="small"
              aria-label={translate('Visualizar')}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ]

  const fetchData = useCallback(
    async (sortField?: string, sortOrder?: 'asc' | 'desc') => {
      if (!startDate || !finishDate) return
      loadingButton.set(true)
      const searchLogData = await getInterestPointSearchLogs({
        action: selectAction.get(),
        userId: userId.get(),
        startDate: startDate,
        finishDate: finishDate,
        origin: originType.get(),
        device_id: selectedDevice.get(),
        station_id: selectedStations.get(),
        sortField,
        sortOrder,
      }).finally(() => {
        loadingButton.set(false)
        expandInterestPoints.set(true)
      })

      setRows(
        searchLogData.data.map((element: any) => {
          return {
            ...element,
            'station:name': element?.station?.name,
            'user:representation_name': element?.user?.representation_name,
            search_info: element?.search_info,
            'interest_point:name': element?.interest_point?.name,
            'device:name': element?.device?.name,
            origin: element?.station?.name
              ? `Estação - ${element?.station?.name}`
              : element?.device?.name
              ? `Dispositivo - ${element?.device?.name}`
              : '',
          }
        })
      )

      setRowCount(searchLogData.rowsCount)
    },
    [
      startDate,
      finishDate,
      selectAction,
      userId,
      loadingButton,
      expandInterestPoints,
      originType,
      selectedDevice,
      selectedStations,
    ]
  )

  const clearFilters = () => {
    setStartDate(null)
    setFinishDate(null)
    initialPeriod.set('')
    userId.set(null)
    selectAction.set(null)
    setRows([])
    setSortModel([])
    setPaginationModel({ page: 0, pageSize: limit })
    expandInterestPoints.set(false)
    originType.set(null)
    selectedDevice.set(null)
    selectedStations.set(null)
  }

  const submit = async () => {
    if (!startDate || !finishDate) {
      notify({
        type: 'error',
        message: translate('Start date and end date must be provided'),
      })
      return false
    }

    setPaginationModel({ page: 0, pageSize: limit })
    await fetchData()
  }

  useEffect(() => {
    if (startDate && finishDate) {
      const sortField = sortModel.length > 0 ? sortModel[0].field : undefined
      const sortOrder =
        sortModel.length > 0 && sortModel[0].sort
          ? (sortModel[0].sort as 'asc' | 'desc')
          : undefined
      fetchData(sortField, sortOrder)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortModel])

  return (
    <>
      <InternalContent
        title={`${translate('Filters')}`}
        sxFont={{ fontWeight: 'bold', fontSize: 18 }}
      >
        <AuditInterestPointFilters
          hidden={expandFilterSearch.get()}
          loadingButton={loadingButton}
          labelButton={labelButton}
          selectAction={selectAction}
          focusChange={focusChange}
          resetValues={resetValeus}
          userId={userId}
          initialPeriod={initialPeriod}
          onPeriodChange={(value) => {
            labelButton.set(translate('Search'))
            initialPeriod.set(value)
          }}
          onStartDateChange={(value) => {
            labelButton.set(translate('Search'))
            setStartDate(value)
          }}
          onFinishDateChange={(value) => {
            labelButton.set(translate('Search'))
            setFinishDate(value)
          }}
          clearFilters={() => clearFilters()}
          searchButton={() => submit()}
          selectedStations={selectedStations}
          selectedDevice={selectedDevice}
          originType={originType}
        />
      </InternalContent>
      <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
        <CardHeader
          title={translate('Actions Performed')}
          avatar={
            <IconButton
              onClick={() =>
                expandInterestPoints.set(!expandInterestPoints.get())
              }
              sx={{
                display: 'flex',
                justifyContent: 'end',
                marginRight: 1,
                padding: 0,
              }}
            >
              {expandInterestPoints.value ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </IconButton>
          }
          titleTypographyProps={{ fontWeight: 'bold', fontSize: 18 }}
        />
        <Collapse in={expandInterestPoints.value}>
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            loading={loadingButton.value}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            columnVisibilityModel={{
              id: false,
              deleted_at: false,
            }}
            disableColumnMenu
            autoHeight
            pagination
            filterMode={tableMode}
            sortingMode={tableMode}
            rowCount={rowCount}
            onSortModelChange={(model) => {
              setSortModel(model)
              setPaginationModel({ page: 0, pageSize: limit })
            }}
            getRowId={(row) => Number(row.id)}
            sx={{
              minHeight: '250px',
              margin: 4,
              [`.${gridClasses.columnHeaderTitle}`]: {
                fontWeight: 'bold !important',
              },
            }}
          />
        </Collapse>
      </Card>
      <Dialog
        fullWidth
        maxWidth={'md'}
        open={openModal}
        onClose={clickOutModalClose}
        disableEscapeKeyDown={true}
      >
        <DialogTitle
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          {translate('Actions Performed')}
          <IconButton aria-label="close" onClick={() => setOpenModal(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ paddingTop: 5 }}>
          {selectedRow && (
            <Grid container xs={12} display={'flex'} flexDirection={'row'}>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('User')}:
                </Typography>
                <Typography variant="subtitle2">
                  {selectedRow['user:representation_name']}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('Consulted origin')}:
                </Typography>
                <Typography variant="subtitle2">
                  {selectedRow.origin}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('action')}:
                </Typography>
                <Typography variant="subtitle2">
                  {translate(selectedRow.action)}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('Performed at')}:
                </Typography>
                <Typography variant="subtitle2">
                  {dayjs(selectedRow.created_at).format('DD/MM/YYYY HH:mm')}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
